import type { VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import Button, { ButtonGroup } from '../../generic/Button';
import { LabeledTextInput } from '../../generic/Forms';
import { useInputValue } from '../../../hooks';
import { onEnter } from '../../../utils';
import type { AsyncStatus } from '../../../types';
import { ErrorAlert } from '../../generic';
import { FILTER_NAME_SIZE_LIMIT } from '../../../../shared/savedFilters';

type Props = {
  isDisplayed: boolean;
  isSaving: boolean;
  errors?: AsyncStatus['errors'];
  onSave: (name: string) => void;
  onHide: () => void;
};

/**
 * SaveAsModal allows the user to save the current filter state as a new filter.
 */
const SaveAsModal: VoidFunctionComponent<Props> = ({ isDisplayed, isSaving, errors, onHide, onSave }) => {
  const [name, setName] = useInputValue();
  const isSubmitDisabled = isSaving || name.trim().length < 1;
  //  Submit on keydown
  const onEnterKeyDown = onEnter(() => !isSubmitDisabled && onSave(name));
  return (
    <Modal animation show={isDisplayed} onHide={onHide} onEscapeKeyDown={onHide}>
      <Modal.Header>
        <Modal.Title>Save filter</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="margin-bottom-md">Your currently applied selections will be saved as a new filter.</div>
        <LabeledTextInput
          label="Name"
          placeholder="Name filter"
          value={name}
          onChange={setName}
          onKeyDown={onEnterKeyDown}
          maxLength={FILTER_NAME_SIZE_LIMIT}
          showCharCount
          autoFocus
          testId="saved-filters-modal-input--save-as"
        />
        {errors && errors.length > 0 && (
          <div className="margin-top-md">
            <ErrorAlert status={{ errors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={() => onSave(name)}
            isDisabled={isSubmitDisabled}
            testId="saved-filters-modal-submit--save-as"
          >
            Save filter
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveAsModal;
